import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Table,
  Tag,
  Progress,
  Alert,
  Typography,
  Select,
  Segmented,
  Button,
  Spin,
} from 'antd';
import {
  ClockCircleOutlined,
  TruckOutlined,
  FileTextOutlined,
  InboxOutlined,
  AuditOutlined,
  EuroOutlined,
  RightOutlined,
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { customerAPI, dashboardAPI } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatCurrency } from '../utils/numberFormatting.ts';
import AlertCard from '../components/dashboard/AlertCard.tsx';
import KpiCard from '../components/dashboard/KpiCard.tsx';
import OrdersTrendChart from '../components/dashboard/OrdersTrendChart.tsx';
import StatusDoughnut from '../components/dashboard/StatusDoughnut.tsx';
import QuotesTrendChart from '../components/dashboard/QuotesTrendChart.tsx';
import { SEMANTIC, ORDER_STATUS_COLORS, SHIPMENT_TYPE_COLORS, CARD_SHADOW } from '../theme/statusColors.ts';

const { Title, Text } = Typography;
const { Option } = Select;

// ---- Tipi risposta API ----
interface LateOrderItem {
  id: number;
  order_number: string;
  customer: string;
  requested_delivery_date: string | null;
  days_late: number;
  total_amount: number;
  pct_reserved: number;  // prenotato (tutte le fonti) / richiesto; prima pct_shipped su una colonna mai scritta (sempre 0)
}

interface ShipmentAlertItem {
  id: number;
  container_code: string;
  shipment_type: string;
  supplier_name: string | null;
  expected_arrival_date: string | null;
  days_to_arrival: number;
  alert_status: 'OVERDUE' | 'WARNING';
}

interface ExpiringQuoteItem {
  id: number;
  quote_number: string;
  customer: string;
  expiry_date: string | null;
  days_to_expiry: number;
  total_amount: number;
}

interface StockAlertItem {
  product_id: number;
  product_code: string;
  product_description: string;
  qta_available: number;
  total_reserved: number;
  net_available: number;
  stock_status: string;
}

interface Im7Item {
  id: number;
  document_number: string;
  document_date: string | null;
  loaded: number;
  used: number;
  remaining: number;
  pct_remaining: number;
}

interface AlertsData {
  late_orders: { count: number; items: LateOrderItem[] };
  shipments: { overdue: number; arriving_soon: number; items: ShipmentAlertItem[] };
  expiring_quotes: { count: number; items: ExpiringQuoteItem[] };
  stock_alerts: { out_of_stock: number; low_stock: number; overbooked: number; items: StockAlertItem[] };
  customs_im7: { low_balance_count: number; draft_count: number; items: Im7Item[] };
}

interface KpiData {
  open_orders_value: number;
  open_orders_count: number;
  period_orders_value: number;
  period_orders_count: number;
  prev_period_delta_pct: number | null;
  inventory_value: number;
  potential_margin: number;
  quotes_sent: number;
  quotes_converted: number;
  conversion_rate_pct: number | null;
}

interface ChartsData {
  orders_by_month: Array<{ month: string; count: number; value: number }>;
  orders_by_status: Array<{ id: number; name: string; count: number }>;
  shipments_by_type: Array<{ type: string; count: number }>;
  quotes_trend: Array<{ month: string; sent: number; accepted: number }>;
}

const fmtDate = (d?: string | null) => (d ? dayjs(d).format('DD/MM/YYYY') : '-');

// Titolo di sezione sobrio (uppercase, secondario)
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Text
    type="secondary"
    style={{ fontSize: 12, letterSpacing: 1, textTransform: 'uppercase', display: 'block', margin: '8px 0' }}
  >
    {children}
  </Text>
);

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [customers, setCustomers] = useState<any[]>([]);
  const [selectedCustomer, setSelectedCustomer] = useState<number | undefined>(undefined);
  const [periodDays, setPeriodDays] = useState<number>(30);

  const [alerts, setAlerts] = useState<AlertsData | null>(null);
  const [kpi, setKpi] = useState<KpiData | null>(null);
  const [charts, setCharts] = useState<ChartsData | null>(null);
  const [loadingAlerts, setLoadingAlerts] = useState(true);
  const [loadingKpi, setLoadingKpi] = useState(true);
  const [loadingCharts, setLoadingCharts] = useState(true);

  const chartMonths = periodDays <= 90 ? 6 : 12;

  // Helper per il messaggio di ambiente (solo admin)
  const getEnvironmentMessage = () => {
    const apiUrl = process.env.REACT_APP_API_URL;
    if (apiUrl === 'http://localhost:8000/api') {
      return { type: 'success' as const, message: 'APPLICAZIONE DI SVILUPPO' };
    } else if (apiUrl === 'https://navi.tebro.it/api') {
      return { type: 'error' as const, message: 'APPLICAZIONE DI PRODUZIONE' };
    }
    return null;
  };

  const loadAlerts = useCallback(async () => {
    setLoadingAlerts(true);
    try {
      setAlerts(await dashboardAPI.getAlerts(selectedCustomer));
    } catch (error) {
      console.error('Errore caricamento alert dashboard:', error);
    } finally {
      setLoadingAlerts(false);
    }
  }, [selectedCustomer]);

  const loadKpi = useCallback(async () => {
    setLoadingKpi(true);
    try {
      setKpi(await dashboardAPI.getKpi(selectedCustomer, periodDays));
    } catch (error) {
      console.error('Errore caricamento KPI dashboard:', error);
    } finally {
      setLoadingKpi(false);
    }
  }, [selectedCustomer, periodDays]);

  const loadCharts = useCallback(async () => {
    setLoadingCharts(true);
    try {
      setCharts(await dashboardAPI.getCharts(selectedCustomer, chartMonths));
    } catch (error) {
      console.error('Errore caricamento grafici dashboard:', error);
    } finally {
      setLoadingCharts(false);
    }
  }, [selectedCustomer, chartMonths]);

  useEffect(() => {
    customerAPI.getCustomers()
      .then(setCustomers)
      .catch((error: any) => console.error('Errore caricamento clienti:', error));
  }, []);

  // Gli alert dipendono solo dal cliente; KPI e grafici anche dal periodo
  useEffect(() => { loadAlerts(); }, [loadAlerts]);
  useEffect(() => { loadKpi(); }, [loadKpi]);
  useEffect(() => { loadCharts(); }, [loadCharts]);

  // ---- Colonne tabelle operative ----
  const lateOrdersColumns = [
    { title: 'Ordine', dataIndex: 'order_number', key: 'order_number', width: 110 },
    { title: 'Cliente', dataIndex: 'customer', key: 'customer', ellipsis: true },
    {
      title: 'Consegna richiesta', dataIndex: 'requested_delivery_date', key: 'rdd', width: 130,
      render: (d: string) => fmtDate(d),
    },
    {
      title: 'Ritardo', dataIndex: 'days_late', key: 'days_late', width: 90, align: 'center' as const,
      render: (v: number) => <Tag color="red">{v} gg</Tag>,
    },
    {
      title: '% prenotata', dataIndex: 'pct_reserved', key: 'pct_reserved', width: 110,
      render: (v: number) => <Progress percent={v} size="small" strokeColor={SEMANTIC.info} />,
    },
  ];

  const shipmentsColumns = [
    { title: 'Codice', dataIndex: 'container_code', key: 'container_code', width: 120 },
    {
      title: 'Tipo', dataIndex: 'shipment_type', key: 'shipment_type', width: 100,
      render: (t: string) => <Tag color={SHIPMENT_TYPE_COLORS[t]}>{t}</Tag>,
    },
    { title: 'Fornitore', dataIndex: 'supplier_name', key: 'supplier_name', ellipsis: true, render: (t: string) => t || '-' },
    {
      title: 'Arrivo previsto', dataIndex: 'expected_arrival_date', key: 'ead', width: 120,
      render: (d: string) => fmtDate(d),
    },
    {
      title: 'Stato', key: 'alert', width: 120, align: 'center' as const,
      render: (r: ShipmentAlertItem) =>
        r.alert_status === 'OVERDUE'
          ? <Tag color="red">RITARDO {Math.abs(r.days_to_arrival)} gg</Tag>
          : <Tag color="orange">ARRIVO IN {r.days_to_arrival} gg</Tag>,
    },
  ];

  const im7Columns = [
    { title: 'Documento', dataIndex: 'document_number', key: 'document_number', ellipsis: true },
    {
      title: 'Data', dataIndex: 'document_date', key: 'document_date', width: 100,
      render: (d: string) => fmtDate(d),
    },
    { title: 'Caricato', dataIndex: 'loaded', key: 'loaded', width: 80, align: 'right' as const },
    { title: 'Usato', dataIndex: 'used', key: 'used', width: 80, align: 'right' as const },
    { title: 'Residuo', dataIndex: 'remaining', key: 'remaining', width: 80, align: 'right' as const },
    {
      title: '% residua', dataIndex: 'pct_remaining', key: 'pct_remaining', width: 130,
      render: (v: number) => (
        <Progress
          percent={v}
          size="small"
          strokeColor={v < 20 ? SEMANTIC.danger : SEMANTIC.success}
        />
      ),
    },
  ];

  const stockColumns = [
    { title: 'Codice', dataIndex: 'product_code', key: 'product_code', width: 130 },
    { title: 'Descrizione', dataIndex: 'product_description', key: 'product_description', ellipsis: true },
    {
      title: 'Disponibile', dataIndex: 'qta_available', key: 'qta_available', width: 95, align: 'center' as const,
      render: (v: number) => <Tag color={v > 0 ? 'green' : 'red'}>{v}</Tag>,
    },
    {
      title: 'Prenotato', dataIndex: 'total_reserved', key: 'total_reserved', width: 90, align: 'center' as const,
      render: (v: number) => <Tag color="orange">{v}</Tag>,
    },
    {
      title: 'Netto', dataIndex: 'net_available', key: 'net_available', width: 80, align: 'center' as const,
      render: (v: number) => <Tag color={v < 0 ? 'purple' : 'default'}>{v}</Tag>,
    },
    {
      title: 'Stato', dataIndex: 'stock_status', key: 'stock_status', width: 130,
      render: (s: string, r: StockAlertItem) => {
        if (r.net_available < 0) return <Tag color="purple">Sovra-prenotato</Tag>;
        if (s === 'out_of_stock') return <Tag color="red">Esaurito</Tag>;
        if (s === 'low_stock') return <Tag color="orange">Scorte basse</Tag>;
        return <Tag>{s}</Tag>;
      },
    },
  ];

  // Card-tabella con titolo e link "vedi tutti"
  const tableCard = (title: string, route: string, table: React.ReactNode) => (
    <Card
      size="small"
      style={{ boxShadow: CARD_SHADOW, height: '100%' }}
      title={<Text strong style={{ fontSize: 13 }}>{title}</Text>}
      extra={
        <Button type="link" size="small" onClick={() => navigate(route)}>
          Vedi tutti <RightOutlined style={{ fontSize: 10 }} />
        </Button>
      }
    >
      {table}
    </Card>
  );

  const emptyText = { emptyText: 'Nessun elemento' };

  return (
    <div
      style={{
        margin: -24,
        padding: 24,
        background: '#f5f6fa',
        borderRadius: 6,
        minHeight: 'calc(100vh - 112px)',
      }}
    >
      {/* Header: titolo + filtri */}
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
        <Title level={2} style={{ margin: 0 }}>Dashboard</Title>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Segmented
            value={periodDays}
            onChange={(v) => setPeriodDays(v as number)}
            options={[
              { label: '30 gg', value: 30 },
              { label: '90 gg', value: 90 },
              { label: '12 mesi', value: 365 },
            ]}
          />
          <Select
            placeholder="Filtra per Cliente"
            style={{ width: 250 }}
            value={selectedCustomer}
            onChange={setSelectedCustomer}
            allowClear
          >
            {customers.map(customer => (
              <Option key={customer.id} value={customer.id}>
                {customer.company}
              </Option>
            ))}
          </Select>
        </div>
      </div>

      {/* Messaggio di ambiente solo per Amministratore (level_id=1) */}
      {user?.level_id === 1 && getEnvironmentMessage() && (
        <Alert
          message={getEnvironmentMessage()?.message}
          type={getEnvironmentMessage()?.type}
          style={{ marginBottom: 16 }}
          showIcon
        />
      )}

      {/* RIGA 1 — Azione richiesta oggi */}
      <SectionTitle>Azione richiesta oggi</SectionTitle>
      <Spin spinning={loadingAlerts}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={12} lg={8}>
            <AlertCard
              title="Ordini cliente in ritardo"
              count={alerts?.late_orders.count ?? 0}
              severity="danger"
              icon={<ClockCircleOutlined />}
              subText="vs data consegna richiesta"
              onClick={() => navigate('/customer-orders')}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <AlertCard
              title="Spedizioni critiche"
              count={(alerts?.shipments.overdue ?? 0) + (alerts?.shipments.arriving_soon ?? 0)}
              severity={alerts && alerts.shipments.overdue > 0 ? 'danger' : 'warning'}
              icon={<TruckOutlined />}
              subText={`${alerts?.shipments.overdue ?? 0} in ritardo · ${alerts?.shipments.arriving_soon ?? 0} in arrivo`}
              onClick={() => navigate('/unified-shipments')}
            />
          </Col>
          <Col xs={24} sm={12} lg={8}>
            <AlertCard
              title="Preventivi in scadenza"
              count={alerts?.expiring_quotes.count ?? 0}
              severity="warning"
              icon={<FileTextOutlined />}
              subText="entro 7 giorni"
              onClick={() => navigate('/customer-quotes')}
            />
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <AlertCard
              title="Giacenze critiche"
              count={(alerts?.stock_alerts.out_of_stock ?? 0) + (alerts?.stock_alerts.low_stock ?? 0) + (alerts?.stock_alerts.overbooked ?? 0)}
              severity={alerts && (alerts.stock_alerts.out_of_stock > 0 || alerts.stock_alerts.overbooked > 0) ? 'danger' : 'warning'}
              icon={<InboxOutlined />}
              subText={`${alerts?.stock_alerts.out_of_stock ?? 0} esauriti · ${alerts?.stock_alerts.low_stock ?? 0} bassi · ${alerts?.stock_alerts.overbooked ?? 0} sovra-pren.`}
              onClick={() => navigate('/inventory')}
            />
          </Col>
          <Col xs={24} sm={12} lg={12}>
            <AlertCard
              title="Dogana: H2 (IM7) / da elaborare"
              count={(alerts?.customs_im7.low_balance_count ?? 0) + (alerts?.customs_im7.draft_count ?? 0)}
              severity="warning"
              icon={<AuditOutlined />}
              subText={`${alerts?.customs_im7.low_balance_count ?? 0} saldi <20% · ${alerts?.customs_im7.draft_count ?? 0} bozze`}
              onClick={() => navigate('/customs-documents')}
            />
          </Col>
        </Row>
      </Spin>

      {/* RIGA 2 — KPI economici */}
      <SectionTitle>Indicatori economici</SectionTitle>
      <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            title="Portafoglio ordini aperti"
            value={kpi?.open_orders_value ?? null}
            prefix={<EuroOutlined style={{ fontSize: 18 }} />}
            subText={kpi ? `${kpi.open_orders_count} ordini da evadere` : undefined}
            loading={loadingKpi}
            onClick={() => navigate('/customer-orders')}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            title={`Ordinato ultimi ${periodDays === 365 ? '12 mesi' : `${periodDays} giorni`}`}
            value={kpi?.period_orders_value ?? null}
            prefix={<EuroOutlined style={{ fontSize: 18 }} />}
            deltaPct={kpi?.prev_period_delta_pct}
            subText={kpi ? `${kpi.period_orders_count} ordini · vs periodo prec.` : undefined}
            loading={loadingKpi}
            onClick={() => navigate('/customer-orders')}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            title="Valore magazzino (disponibile)"
            value={kpi?.inventory_value ?? null}
            prefix={<EuroOutlined style={{ fontSize: 18 }} />}
            subText={kpi ? `Margine potenziale: € ${formatCurrency(kpi.potential_margin)}` : undefined}
            loading={loadingKpi}
            onClick={() => navigate('/inventory')}
          />
        </Col>
        <Col xs={24} sm={12} xl={6}>
          <KpiCard
            title="Conversione preventivi"
            value={kpi?.conversion_rate_pct ?? null}
            suffix="%"
            precision={1}
            subText={kpi ? `${kpi.quotes_converted} su ${kpi.quotes_sent} emessi nel periodo` : undefined}
            loading={loadingKpi}
            onClick={() => navigate('/customer-quotes')}
          />
        </Col>
      </Row>

      {/* RIGA 3 — Grafici */}
      <SectionTitle>Andamento</SectionTitle>
      <Spin spinning={loadingCharts}>
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} xl={12}>
            <Card size="small" style={{ boxShadow: CARD_SHADOW }} title={<Text strong style={{ fontSize: 13 }}>Ordinato mensile</Text>}>
              <OrdersTrendChart data={charts?.orders_by_month ?? []} />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" style={{ boxShadow: CARD_SHADOW }} title={<Text strong style={{ fontSize: 13 }}>Ordini aperti per stato</Text>}>
              <StatusDoughnut
                data={(charts?.orders_by_status ?? []).map(s => ({
                  label: s.name,
                  count: s.count,
                  color: ORDER_STATUS_COLORS[s.id] || SEMANTIC.neutral,
                }))}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} xl={6}>
            <Card size="small" style={{ boxShadow: CARD_SHADOW }} title={<Text strong style={{ fontSize: 13 }}>Spedizioni attive per tipo</Text>}>
              <StatusDoughnut
                data={(charts?.shipments_by_type ?? []).map(s => ({
                  label: s.type,
                  count: s.count,
                  color: SHIPMENT_TYPE_COLORS[s.type] || SEMANTIC.neutral,
                }))}
              />
            </Card>
          </Col>
          <Col xs={24} xl={12}>
            <Card size="small" style={{ boxShadow: CARD_SHADOW }} title={<Text strong style={{ fontSize: 13 }}>Preventivi: emessi vs accettati</Text>}>
              <QuotesTrendChart data={charts?.quotes_trend ?? []} />
            </Card>
          </Col>
        </Row>
      </Spin>

      {/* RIGA 4 — Liste operative */}
      <SectionTitle>Operatività</SectionTitle>
      <Spin spinning={loadingAlerts}>
        <Row gutter={[16, 16]}>
          <Col xs={24} xl={12}>
            {tableCard('Ordini cliente in ritardo', '/customer-orders', (
              <Table
                columns={lateOrdersColumns}
                dataSource={alerts?.late_orders.items ?? []}
                rowKey="id"
                size="small"
                pagination={false}
                locale={emptyText}
                onRow={() => ({ onClick: () => navigate('/customer-orders'), style: { cursor: 'pointer' } })}
              />
            ))}
          </Col>
          <Col xs={24} xl={12}>
            {tableCard('Spedizioni in ritardo / in arrivo', '/unified-shipments', (
              <Table
                columns={shipmentsColumns}
                dataSource={alerts?.shipments.items ?? []}
                rowKey="id"
                size="small"
                pagination={false}
                locale={emptyText}
                onRow={() => ({ onClick: () => navigate('/unified-shipments'), style: { cursor: 'pointer' } })}
              />
            ))}
          </Col>
          <Col xs={24} xl={12}>
            {tableCard('Saldi H2 (IM7)', '/customs-documents', (
              <Table
                columns={im7Columns}
                dataSource={alerts?.customs_im7.items ?? []}
                rowKey="id"
                size="small"
                pagination={false}
                locale={emptyText}
                onRow={() => ({ onClick: () => navigate('/customs-documents'), style: { cursor: 'pointer' } })}
              />
            ))}
          </Col>
          <Col xs={24} xl={12}>
            {tableCard('Alert giacenze', '/inventory', (
              <Table
                columns={stockColumns}
                dataSource={alerts?.stock_alerts.items ?? []}
                rowKey="product_id"
                size="small"
                pagination={false}
                locale={emptyText}
                onRow={() => ({ onClick: () => navigate('/inventory'), style: { cursor: 'pointer' } })}
              />
            ))}
          </Col>
        </Row>
      </Spin>
    </div>
  );
};

export default Dashboard;
